import { FEATURE_NOT_IN_PLAN, MAINTENANCE, SUBSCRIPTION_REQUIRED } from "./codes";
import type { ApiRefusal } from "./refusal";

/** What the console does about a refusal. `OTHER` is shown with the generic wording for its code. */
export type RefusalCase =
  | { readonly kind: "SIGNED_OUT" }
  | { readonly kind: "SUBSCRIPTION_REQUIRED" }
  | { readonly kind: "FEATURE_NOT_IN_PLAN"; readonly entitlement?: string; readonly planNeeded?: string }
  | { readonly kind: "CONFLICT"; readonly code: string }
  | { readonly kind: "RATE_LIMITED"; readonly retryAfter?: number }
  | {
      readonly kind: "MAINTENANCE";
      /** When the work should end, ISO UTC. Left out when the edge has no time to promise. */
      readonly until?: string;
      /** True once the end time has passed with the work still going; the edge then sends no `until`. */
      readonly overdue?: boolean;
      readonly mode?: string;
      /** Which of the approved "what keeps working" lines applies. */
      readonly keepsWorkingKey?: string;
      readonly note?: string;
      readonly retryAfter?: number;
    }
  | { readonly kind: "OTHER"; readonly status: number; readonly code: string };

function paramText(refusal: ApiRefusal, name: string): string | undefined {
  const value = refusal.params[name];
  return typeof value === "string" ? value : undefined;
}

/** Sorts a refusal into the few cases the console handles in one shared way. */
export function classifyRefusal(refusal: ApiRefusal): RefusalCase {
  const { status, code } = refusal;
  if (status === 401) return { kind: "SIGNED_OUT" };
  if (status === 402 && code === SUBSCRIPTION_REQUIRED) return { kind: "SUBSCRIPTION_REQUIRED" };
  if (status === 403 && code === FEATURE_NOT_IN_PLAN) {
    const entitlement = paramText(refusal, "entitlement");
    const planNeeded = paramText(refusal, "planNeeded");
    return {
      kind: "FEATURE_NOT_IN_PLAN",
      ...(entitlement === undefined ? {} : { entitlement }),
      ...(planNeeded === undefined ? {} : { planNeeded }),
    };
  }
  if (status === 503 && code === MAINTENANCE) {
    const until = paramText(refusal, "until");
    const mode = paramText(refusal, "mode");
    const keepsWorkingKey = paramText(refusal, "keepsWorkingKey");
    const note = paramText(refusal, "note");
    const overdue = refusal.params["overdue"] === true;
    return {
      kind: "MAINTENANCE",
      ...(until === undefined ? {} : { until }),
      ...(overdue ? { overdue } : {}),
      ...(mode === undefined ? {} : { mode }),
      ...(keepsWorkingKey === undefined ? {} : { keepsWorkingKey }),
      ...(note === undefined ? {} : { note }),
      ...(refusal.retryAfter === undefined ? {} : { retryAfter: refusal.retryAfter }),
    };
  }
  if (status === 409) return { kind: "CONFLICT", code };
  if (status === 429) return { kind: "RATE_LIMITED", ...(refusal.retryAfter === undefined ? {} : { retryAfter: refusal.retryAfter }) };
  return { kind: "OTHER", status, code };
}
